/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import store from 'js/lib/coursera.store';

import { ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

const panelExpandedWidthPx = 330;
const panelCollapsedWidthPx = 24;
const DEFAULT_LEFT_PANEL_RENDER_COLLAPSED_KEY = 'quick-grader-left-panel-collapsed';

const styles = {
  parentContainer: () => css`
    position: relative;
    height: 100%;
    width: ${panelExpandedWidthPx}px;
    min-width: ${panelExpandedWidthPx}px;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-right: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    z-index: 5;
    transition: width 0.35s, min-width 0.35s;
  `,
  panelCollapsed: () => css`
    width: ${panelCollapsedWidthPx}px;
    min-width: ${panelCollapsedWidthPx}px;
  `,
  panelExpanded: () => css`
    width: ${panelExpandedWidthPx}px;
    min-width: ${panelExpandedWidthPx}px;
  `,
  expandButtonContainer: () => css`
    position: absolute;
    top: 16px;
    right: -16px;
  `,
  button: () => css`
    display: flex;
    align-items: center;
    width: 32px;
    height: 32px;
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    box-sizing: border-box;
    border-radius: 20px;
    font-size: 14px;
    padding: var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);

    &:focus {
      outline: none;
      text-decoration: none;
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus);
      box-sizing: border-box;
    }
  `,
  childContainer: () => css`
    width: 100%;
    height: 100%;
    overflow: hidden;
    white-space: nowrap;
    transition: opacity 0.35s, visibility 0.35s;
  `,
  childCollapsed: () => css`
    opacity: 0;
    visibility: hidden;
  `,
  childExpanded: () => css`
    opacity: 1;
    visibility: visible;
  `,
};

type Props = {
  renderCollapsed?: boolean;
  children?: React.ReactNode;
};

const LeftPanel: React.FC<Props> = ({ renderCollapsed = false, children }) => {
  const [panelIsCollapsed, setPanelIsCollapsed] = useState(
    store.get(DEFAULT_LEFT_PANEL_RENDER_COLLAPSED_KEY) || renderCollapsed
  );
  const toggle = () => {
    setPanelIsCollapsed(!panelIsCollapsed);
    store.set(DEFAULT_LEFT_PANEL_RENDER_COLLAPSED_KEY, !panelIsCollapsed);
  };

  return (
    <div
      css={[styles.parentContainer, panelIsCollapsed ? styles.panelCollapsed : styles.panelExpanded]}
      data-testid="left-panel-parent-container"
    >
      <div css={styles.expandButtonContainer}>
        <button
          id="grading-left-panel-expand-button"
          aria-controls="grading-left-panel-container"
          aria-expanded={!panelIsCollapsed}
          type="button"
          css={styles.button}
          onClick={toggle}
        >
          {panelIsCollapsed ? (
            <ChevronNextIcon title="Expand" size="small" />
          ) : (
            <ChevronPreviousIcon title="Collapse" size="small" />
          )}
        </button>
      </div>
      <div
        id="grading-left-panel-container"
        aria-labelledby="grading-left-panel-expand-button"
        css={[styles.childContainer, panelIsCollapsed ? styles.childCollapsed : styles.childExpanded]}
      >
        {children}
      </div>
    </div>
  );
};

export { LeftPanel, panelExpandedWidthPx, panelCollapsedWidthPx };
